import { useObservableEagerState } from 'observable-hooks';
import {
    combineLatest,
    firstValueFrom,
    map,
    of,
    shareReplay,
    startWith,
    type Observable,
} from 'rxjs';
import type { AnySchema } from 'yup';
import { BaseReportElement } from '../../BaseReportElement';
import { MarkdownPreview } from '../../../../../../utils/markdown';
import { createMarkdownStatus } from '../../../../../../utils/markdownTransforms';
import { getBindingCatalog } from '../../../runtime/dataServices/bindingCatalog';
import { assertRuntimeBindingSupportsContextKind } from '../../../runtime/dataServices/bindingCompatibility';
import { createBindingId } from '../../../runtime/dataServices/bindingId';
import type { ActiveDataBinding, BindingId } from '../../../runtime/dataServices/types';
import { createRuntimeBindingInsertionPresetsForKind } from '../../runtimeBindingPresetUtils';
import type {
    ContextForKind,
    ElementDefinitionWithSettings,
    ElementContentRenderResult,
    ElementRenderState,
    ElementSettings,
    ElementSettingsFormSchema,
    RenderContextKind,
    RenderRuntime,
    ResolveElementSettingsFormSchemaArgs,
} from '../../types';

interface RichTextSettingsInput extends ElementSettings {
    text: unknown;
    alignment?: unknown;
    marginTop?: unknown;
    marginBottom?: unknown;
}

type RichTextAlignment = 'left' | 'center' | 'right';

interface RichTextSettings extends ElementSettings {
    text: string;
    alignment?: RichTextAlignment;
    marginTop?: number;
    marginBottom?: number;
}

export type RichTextDefinition = ElementDefinitionWithSettings<'text', RichTextSettings>;

const DEFAULT_RICH_TEXT_CONTENT = `# ${gettext('MARKDOWN_EDITOR_DEFAULT_HEADING')}\n\n${gettext(
    'ELEMENT_RICH_TEXT_PRESET_DEFAULT_BODY'
)}`;
const PLACEHOLDER_PATTERN = /{{\s*([^{}]+?)\s*}}/g;
const INTERPOLATED_PLACEHOLDER_PATTERN =
    /\*\*[ \t]*{{\s*([^{}]+?)\s*}}[ \t]*\*\*|{{\s*([^{}]+?)\s*}}/g;

type ResolvedScalarBinding =
    | { kind: 'value'; value: string }
    | { kind: 'null'; previewFallback?: string };
type ResolvedBindingIdEntry = readonly [string, ResolvedScalarBinding];

interface ParsedBindingPlaceholder {
    bindingId: BindingId;
    arg?: string;
    instanceKey: string;
}

export function createRichTextDefinition(text: string): RichTextDefinition {
    return {
        type: 'text',
        settings: { text },
    };
}

function createScalarRichTextInsertionPresets() {
    return createRuntimeBindingInsertionPresetsForKind({
        valueKind: 'scalar',
        createDefinitionForBindingId: (bindingId) =>
            createRichTextDefinition(`**{{${bindingId}}}**`),
    });
}

function isPrimitiveValue(value: unknown): value is string | number | boolean {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function parseBindingPlaceholder(rawPlaceholder: string): ParsedBindingPlaceholder {
    const parts = rawPlaceholder.split(':');
    if (parts.length > 2) {
        throw new Error(
            `Invalid binding placeholder "{{${rawPlaceholder}}}". Expected "{{bindingId}}" or "{{bindingId:arg}}".`
        );
    }

    const bindingId = parts[0].trim();
    if (!bindingId.length) {
        throw new Error(
            `Invalid binding placeholder "{{${rawPlaceholder}}}": binding id is required.`
        );
    }

    const rawArg = parts[1];
    if (typeof rawArg === 'undefined') {
        return {
            bindingId: bindingId as BindingId,
            instanceKey: bindingId,
        };
    }

    const arg = rawArg.trim();
    if (!arg.length) {
        throw new Error(
            `Invalid binding placeholder "{{${rawPlaceholder}}}": argument is required.`
        );
    }

    return {
        bindingId: bindingId as BindingId,
        arg,
        instanceKey: `${bindingId}:${arg}`,
    };
}

function parseBindingPlaceholders(text: string): readonly ParsedBindingPlaceholder[] {
    const placeholdersByInstanceKey = new Map<string, ParsedBindingPlaceholder>();
    Array.from(text.matchAll(PLACEHOLDER_PATTERN)).forEach(([, rawPlaceholder]) => {
        const placeholder = parseBindingPlaceholder(rawPlaceholder);
        placeholdersByInstanceKey.set(placeholder.instanceKey, placeholder);
    });
    return Array.from(placeholdersByInstanceKey.values());
}

function resolveTextRuntimeBindings(text: string): readonly ActiveDataBinding[] {
    const bindingCatalog = getBindingCatalog();
    return parseBindingPlaceholders(text).map((placeholder) => {
        const descriptor = bindingCatalog.getByIdAndValueKindOrThrow(
            placeholder.bindingId,
            'scalar'
        );
        return bindingCatalog.toActiveDataBinding(descriptor, placeholder.arg);
    });
}

function assertTextRuntimeBindingsForContext(
    text: string,
    contextKind: RenderContextKind
): readonly ActiveDataBinding[] {
    const bindings = resolveTextRuntimeBindings(text);
    const bindingCatalog = getBindingCatalog();

    bindings.forEach((binding) => {
        const descriptor = bindingCatalog.getByIdOrThrow(createBindingId(binding));
        assertRuntimeBindingSupportsContextKind(descriptor.bindingId, descriptor.link, contextKind);
    });

    return bindings;
}

type RichTextSettingsSchemaContext = RenderContextKind | 'context-agnostic';

function buildRuntimeBindingValidationSchema(
    baseSchema: AnySchema,
    schemaContext: RichTextSettingsSchemaContext
): AnySchema {
    return baseSchema.test(
        'valid-rich-text-runtime-bindings',
        'Invalid Rich Text runtime binding.',
        function validateRuntimeBindings(value) {
            if (typeof value !== 'string') {
                return true;
            }

            try {
                if (schemaContext === 'context-agnostic') {
                    resolveTextRuntimeBindings(value);
                } else {
                    assertTextRuntimeBindingsForContext(value, schemaContext);
                }
                return true;
            } catch (error) {
                if (!(error instanceof Error)) {
                    throw error;
                }

                return this.createError({
                    message:
                        schemaContext === 'context-agnostic'
                            ? error.message
                            : gettext('INVALID_RICH_TEXT_RUNTIME_BINDING'),
                });
            }
        }
    );
}

function createRichTextSettingsFormSchema(
    schemaContext: RichTextSettingsSchemaContext
): ElementSettingsFormSchema {
    return {
        fields: [
            {
                name: 'text',
                label: gettext('ELEMENT_TEXT_LABEL'),
                type: 'markdown',
                required: true,
                default: DEFAULT_RICH_TEXT_CONTENT,
                buildValidationSchema: (baseSchema: AnySchema) =>
                    buildRuntimeBindingValidationSchema(baseSchema, schemaContext),
            },
            {
                name: 'alignment',
                label: gettext('ELEMENT_TEXT_ALIGNMENT_LABEL'),
                type: 'choice',
                required: false,
                options: [
                    ['', gettext('ELEMENT_LAYOUT_ALIGNMENT_DEFAULT')],
                    ['left', gettext('ELEMENT_LAYOUT_ALIGNMENT_LEFT')],
                    ['center', gettext('ELEMENT_LAYOUT_ALIGNMENT_CENTER')],
                    ['right', gettext('ELEMENT_LAYOUT_ALIGNMENT_RIGHT')],
                ],
                default: null,
            },
            {
                name: 'marginTop',
                label: gettext('ELEMENT_LAYOUT_MARGIN_TOP_LABEL'),
                type: 'float',
                required: false,
                minValue: 0,
                default: null,
            },
            {
                name: 'marginBottom',
                label: gettext('ELEMENT_LAYOUT_MARGIN_BOTTOM_LABEL'),
                type: 'float',
                required: false,
                minValue: 0,
                default: null,
            },
        ],
    };
}

function observeInterpolatedContextPlaceholders(
    text: string,
    ctx: ContextForKind<RenderContextKind>,
    runtime: RenderRuntime
): Observable<string> {
    // Keep a single observer per binding instance for this text block; duplicate
    // placeholders are replaced from the same resolved value.
    const placeholders = parseBindingPlaceholders(text);
    if (placeholders.length === 0) {
        return of(text);
    }

    const bindingCatalog = getBindingCatalog();

    return combineLatest(
        placeholders.map((placeholder): Observable<ResolvedBindingIdEntry> => {
            const descriptor = bindingCatalog.getByIdAndValueKindOrThrow(
                placeholder.bindingId,
                'scalar'
            );
            const binding = bindingCatalog.toActiveDataBinding(descriptor, placeholder.arg);

            return runtime.observeBinding(binding, { context: ctx }).pipe(
                map((linkValue): ResolvedBindingIdEntry => {
                    if (linkValue !== null && !isPrimitiveValue(linkValue)) {
                        throw new Error(
                            `Expected scalar placeholder "${placeholder.instanceKey}" to resolve to string, number, or boolean.`
                        );
                    }

                    return linkValue === null
                        ? [
                              placeholder.instanceKey,
                              {
                                  kind: 'null',
                                  previewFallback: descriptor.link.nullPreviewFallback,
                              },
                          ]
                        : [placeholder.instanceKey, { kind: 'value', value: String(linkValue) }];
                })
            );
        })
    ).pipe(
        map((resolvedBindingIdEntries) => {
            const resolvedByInstanceKey = new Map<string, ResolvedScalarBinding>(
                resolvedBindingIdEntries
            );
            return text.replace(
                INTERPOLATED_PLACEHOLDER_PATTERN,
                (
                    match,
                    rawBoldPlaceholder: string | undefined,
                    rawPlainPlaceholder: string | undefined
                ) => {
                    const rawPlaceholder = rawBoldPlaceholder ?? rawPlainPlaceholder;
                    if (typeof rawPlaceholder === 'undefined') {
                        throw new Error('Missing runtime binding placeholder capture.');
                    }

                    const placeholder = parseBindingPlaceholder(rawPlaceholder);
                    const resolvedBinding = resolvedByInstanceKey.get(placeholder.instanceKey);
                    if (!resolvedBinding) {
                        throw new Error(
                            `Missing resolved scalar binding "${placeholder.instanceKey}".`
                        );
                    }

                    if (resolvedBinding.kind === 'null') {
                        // Export always drops null scalar values because leftover template syntax
                        // in a customer-facing PDF reads as a broken report. Authoring surfaces
                        // preserve the placeholder by default, while individual bindings can opt
                        // into a friendlier preview fallback below.
                        if (runtime.renderSurface === 'export') {
                            return '';
                        }

                        // Opt-in null fallbacks represent a status rather than bound content.
                        // Render them as an inline badge instead of inheriting author-applied
                        // formatting from the placeholder.
                        return resolvedBinding.previewFallback
                            ? createMarkdownStatus(resolvedBinding.previewFallback)
                            : match;
                    }

                    return typeof rawBoldPlaceholder === 'string'
                        ? resolvedBinding.value && `**${resolvedBinding.value}**`
                        : resolvedBinding.value;
                }
            );
        })
    );
}

interface RichTextRendererProps {
    renderedText$: Observable<string>;
    alignment?: RichTextAlignment;
    marginTop?: number;
    marginBottom?: number;
}

function RichTextRenderer({
    renderedText$,
    alignment,
    marginTop,
    marginBottom,
}: RichTextRendererProps): JSX.Element {
    const renderedText = useObservableEagerState(renderedText$);
    const hasLayoutSettings =
        typeof alignment !== 'undefined' ||
        typeof marginTop !== 'undefined' ||
        typeof marginBottom !== 'undefined';

    return (
        <div className="flow-root">
            <MarkdownPreview
                markdownText={renderedText}
                style={
                    hasLayoutSettings
                        ? { textAlign: alignment, marginTop, marginBottom }
                        : undefined
                }
            />
        </div>
    );
}

export class RichTextElement extends BaseReportElement<
    RenderContextKind,
    RichTextSettingsInput,
    RichTextSettings
> {
    static readonly elementType = 'text';

    static readonly layout = { flow: 'fixed' } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_TEXT_LABEL'),
            supportedSlots: ['child-list'] as const,
            createDefinition: () => createRichTextDefinition(DEFAULT_RICH_TEXT_CONTENT),
            presets: [
                {
                    key: 'blank',
                    label: gettext('ELEMENT_TEXT_PRESET_BLANK_LABEL'),
                    description: gettext('ELEMENT_TEXT_PRESET_BLANK_DESCRIPTION'),
                    compatibleContextKinds: ['project', 'measuring-point'] as const,
                    supportedSwarmTypes: null,
                    createDefinition: () => createRichTextDefinition(DEFAULT_RICH_TEXT_CONTENT),
                },
                ...createScalarRichTextInsertionPresets(),
            ],
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        return createRichTextSettingsFormSchema('context-agnostic');
    }

    static resolveSettingsFormSchema({
        contextKind,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        return Promise.resolve(createRichTextSettingsFormSchema(contextKind));
    }

    protected normalizeSettings(rawSettings: RichTextSettingsInput): RichTextSettings {
        const { text, alignment, marginTop, marginBottom } = super.normalizeSettings(rawSettings);

        return {
            text,
            ...(alignment ? { alignment } : {}),
            ...(typeof marginTop === 'number' ? { marginTop } : {}),
            ...(typeof marginBottom === 'number' ? { marginBottom } : {}),
        };
    }

    protected getRuntimeBindings(): readonly ActiveDataBinding[] {
        return resolveTextRuntimeBindings(this.settings.text);
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime,
        { isSwarmCompatible }: ElementRenderState
    ): ElementContentRenderResult {
        const renderedText$ = (
            isSwarmCompatible
                ? observeInterpolatedContextPlaceholders(this.settings.text, ctx, runtime)
                : of(this.settings.text)
        ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
        const renderedTextWithInitial$ = renderedText$.pipe(startWith(this.settings.text));

        return {
            node: (
                <RichTextRenderer
                    renderedText$={renderedTextWithInitial$}
                    alignment={this.settings.alignment}
                    marginTop={this.settings.marginTop}
                    marginBottom={this.settings.marginBottom}
                />
            ),
            ready: firstValueFrom(renderedText$).then(() => undefined),
            hasRenderableContent: Promise.resolve(true),
        };
    }
}
